import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, Row, Spinner } from "../../Components/Common/Tailwind";
import { authService } from "../../api/services/auth.service";
import { setAccessToken, setRefreshToken } from "../../api/auth";
import MobileViewContainer from "../../Components/MobileViewContainer";
import { useAuthContext } from "../../Components/AuthContext";
import { useUserStore } from "../../store/store";

const KakaoPhoneInput = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [kakaoToken, setKakaoToken] = useState(null);
  const [site] = useUserStore((state) => [state.site]);

  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuthContext();

  useEffect(() => {
    // Get Kakao token from localStorage or location state
    const storedToken = localStorage.getItem("kakao_pending_token");
    const stateToken = location.state?.kakao_access_token;

    const token = stateToken || storedToken;

    if (!token) {
      setError("카카오 로그인 정보를 찾을 수 없습니다. 다시 로그인해주세요.");
      return;
    }

    setKakaoToken(token);

    // Store in localStorage if it came from state
    if (stateToken && !storedToken) {
      localStorage.setItem("kakao_pending_token", stateToken);
    }
  }, [location.state]);

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    // Only allow numbers
    const numbersOnly = value.replace(/[^0-9]/g, '');
    setPhoneNumber(numbersOnly);
    setError(null);
  };

  const validatePhoneNumber = () => {
    // Korean phone number validation: 11 digits starting with 010/011/016/017/018/019
    const phoneRegex = /^01[016789]\d{7,8}$/;

    if (!phoneNumber) {
      setError("전화번호를 입력해주세요.");
      return false;
    }

    if (phoneNumber.length !== 11) {
      setError("전화번호는 11자리여야 합니다.");
      return false;
    }

    if (!phoneRegex.test(phoneNumber)) {
      setError("올바른 전화번호 형식이 아닙니다. (예: 01012345678)");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePhoneNumber()) {
      return;
    }

    if (!kakaoToken) {
      setError("카카오 로그인 정보가 없습니다. 다시 로그인해주세요.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.completeKakaoLogin(kakaoToken, phoneNumber);

      if (response.data?.access_token && response.data?.refresh_token) {
        // Store tokens
        setAccessToken(response.data.access_token);
        setRefreshToken(response.data.refresh_token);

        // Clear pending token
        localStorage.removeItem("kakao_pending_token");

        // Update auth context
        if (setUser) {
          setUser(response.data);
        }

        // Redirect to original destination or my page
        const redirectTo = localStorage.getItem("redirectTo");
        if (redirectTo && redirectTo.trim()) {
          localStorage.removeItem("redirectTo");
          navigate(redirectTo);
        } else {
          navigate("/my-page");
        }
      } else {
        throw new Error("로그인 응답이 올바르지 않습니다.");
      }
    } catch (err) {
      console.error("Complete Kakao login error:", err);

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (detail.includes("phone number")) {
          setError("이미 사용 중인 전화번호입니다.");
        } else if (detail.includes("Invalid")) {
          setError("카카오 로그인 정보가 만료되었습니다. 다시 로그인해주세요.");
        } else {
          setError(detail);
        }
      } else {
        setError("전화번호 등록 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <MobileViewContainer>
        <div className="flex h-screen items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            {/* Top Section: Logo & Welcome */}
            <div className="w-full mt-5 px-4 text-center">
              <div className="mb-4 flex justify-center">
                <Link to="/" className="inline-block">
                  <img
                    src={site?.logo ? site.logo : `/amazing-logo.png`}
                    alt="Logo"
                    className="rounded-full"
                    style={{
                      height: "72px",
                      width: "auto",
                      maxWidth: "100%",
                      objectFit: "contain",
                      filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.08))"
                    }}
                  />
                </Link>
              </div>
              <h2 className="font-bold mb-2 text-center" style={{ color: "#1A1A1A", fontSize: "22px", letterSpacing: "-1px" }}>
                {site?.name || "어메이징 BBQ"}
              </h2>
              <p className="text-gray-500 mb-0 text-center" style={{ fontSize: "16px", fontWeight: "400", opacity: 0.8 }}>
                {site?.slogan || "세상에 없던 놀이터, 놀다 지쳐 쓰러지리라!"}
              </p>
            </div>

            <Row className="mt-4">
              <Col lg={12}>
                <Card className="bg-white rounded-2xl shadow-lg border border-amber-100">
                  <CardBody className="p-6 sm:p-8">
                    <h4 className="text-left text-xl font-semibold text-gray-900">
                      전화번호를 등록해 마무리하기
                    </h4>
                    <p className="mt-2 text-sm text-gray-500">
                      카카오 계정 인증 후, 서비스 이용을 위해 번호 확인이 필요합니다.
                    </p>

                    <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
                      실시간 알림과 예매 확인을 위해 정확한 번호를 입력해주세요.
                    </div>

                    <form onSubmit={handleSubmit} className="mt-5">
                      <div className="mb-4">
                        <label htmlFor="phone" className="form-label text-gray-700">
                          휴대폰 번호 <span className="text-red-500">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="tel"
                            id="phone"
                            maxLength={11}
                            className="form-control"
                            placeholder="01012345678"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                            disabled={isLoading}
                          />
                        </div>
                        <small className="text-gray-500 mt-1">
                          숫자만 입력 (- 없이 11자리)
                        </small>
                      </div>

                      {error && (
                        <Alert color="danger" className="mb-4">
                          {error}
                        </Alert>
                      )}

                      <div className="mt-5">
                        <Button
                          type="submit"
                          color="primary"
                          className="w-full"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              처리 중...
                            </>
                          ) : (
                            "완료하고 시작하기"
                          )}
                        </Button>
                      </div>
                    </form>

                    <div className="mt-4 text-center text-sm text-gray-500">
                      이미 계정이 있나요?{" "}
                      <Link to="/login" className="text-amber-700 hover:text-amber-800">
                        로그인 화면으로 돌아가기
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </MobileViewContainer>
    </React.Fragment>
  );
};

export default KakaoPhoneInput;
